import { useEffect } from "react";
import type { SignInError } from "plg/screens/Signup/authStartResult";
import { trackPlg } from "plg/analytics/events";
import { LOGIN_ROUTE, PLG_SIGNUP_ROUTE } from "constants/routes";

interface Props {
  error: SignInError | null;
  /** error_shown.screen */
  screen?: string;
}

type NoteError = Exclude<SignInError, "exists" | "adminExists" | "converted" | "country" | "blocked" | "human">;

const NOTES: Record<NoteError, [string | null, string]> = {
  suspended: ["This account is suspended", "Contact Edge if you think this is a mistake."],
  disposable: ["That address will not work", "We cannot reach that inbox. Try a different email."],
  unverified: ["We could not verify that account", "Try another one."],
  magicInvalid: ["This link has expired or was already used", "Sign-in links work once and expire after 15 minutes."],
  ipLimit: ["We can't create another account from this network today", "Please try again tomorrow."],
  plusEmail: ["That address will not work", "Use your email address without a \"+\" tag."],
  generic: [null, "Something went wrong. Please try again."],
};

/** The notes under the sign-in buttons, one per SignInError ("country" / "blocked" replace the card; "human" re-shows the gate). */
function SignInErrorNote({ error, screen = "sign_in" }: Props) {
  const shown = !!error && error !== "country" && error !== "blocked" && error !== "human";
  useEffect(() => {
    if (shown) trackPlg("error_shown", { error_type: error as string, screen });
  }, [shown, error, screen]);
  if (!error || error === "country" || error === "blocked" || error === "human") return null;
  if (error === "exists") {
    return (
      <div className="plg-signup__note" role="alert">
        <b>You already have a Portal Account</b>
        Please contact your RM to enable this feature for you on the Edge Portal.
      </div>
    );
  }
  if (error === "adminExists") {
    return (
      <div className="plg-signup__note" role="alert">
        <b>You already have a portal account</b>
      </div>
    );
  }
  if (error === "converted") {
    return (
      <div className="plg-signup__note" role="alert">
        <b>Your account has moved to the portal</b>
        Sign in on the portal login page. <a href={LOGIN_ROUTE}>Log in</a>
      </div>
    );
  }
  const [title, body] = NOTES[error];
  return (
    <div className="plg-signup__note" role="alert">
      {title && <b>{title}</b>}
      {body}
      {error === "magicInvalid" && (
        <>
          {" "}
          <a href={PLG_SIGNUP_ROUTE}>Sign in again</a>
        </>
      )}
    </div>
  );
}

export default SignInErrorNote;
